import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { get } from 'lodash'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
  Factsheet,
  Factsheet_Effectiveness_Evaluation,
  File as GraphqlFile,
  Maybe,
  Query_Root,
  User,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  CATEGORY_ID_PREFIX,
  fetchFactsheetEffectivenessClassificationData,
} from 'src/screens/factsheets/factsheet/FactsheetEffectivenessClassificationUtils'
import {
  fetchFactsheetByIdQuery,
  fetchFactsheetByIdWithEffectivenessEvaluations,
} from 'src/screens/factsheets/factsheet/factsheetQueries'
import {
  EvaluationFileItem,
  EvidenceOfEffectivenessFilesGrid,
} from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFilesGrid'
import { mapToEvaluationFileItem } from 'src/screens/shared/evidence-of-effectiveness/utils/EvidenceOfEffectivenessUtils'
import { usePermissionsForFactsheetDetailsDescriptionPage } from 'src/service/security/PermissionHook'
import { EditButton } from 'src/shared/button/Buttons'
import { ENTITY_TYPE, FACTSHEET_USER_ROLES, FILE_TYPE, USER_STATUS } from 'src/shared/constants/constants'
import { FACTSHEET_DESCRIPTION_EDIT_OPTION, FACTSHEET_WORKFLOW_STATUS } from 'src/shared/constants/factsheet-constants'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { FactsheetExportMenu } from 'src/shared/menu/FactsheetExportMenu'
import { DefaultSectionTypography } from 'src/shared/presentation/DefaultSectionTypography'
import { queryFiles } from 'src/shared/presentation/fileQueries'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { ReadOnlySelection, ReadOnlyTextField } from 'src/shared/presentation/ReadOnly'
import { Section } from 'src/shared/presentation/Section'
import { S } from 'src/shared/styled/S'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { useUser, useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient, useQuery } from 'urql'

const IdChip = styled(Chip)`
  cursor: pointer;
  min-width: 3rem;

  &.MuiChip-filledDefault {
    background-color: ${({ theme }) => theme.colors.action.hover};
    color: ${({ theme }) => theme.colors.text.disabled};
  }

  & .MuiChip-label {
    ${TYPOGRAPHY_MIXIN.subtitle2};
  }
`

type FactsheetClassificationDataState = {
  categories: Array<{
    categoryId: number
    categoryName: string
    score: string | undefined
  }>
  reason?: string
}

export const FactsheetDescriptionPage = (): ReactElement => {
  const { factsheetId } = useParams()
  const factsheet_id = parseInt(factsheetId as string)
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const { user } = useUser()
  const locale = useUserLocale()
  const userRoles = user.roles

  const notificationService = useNotificationService()
  const language = useUserLocale()
  const navigate = useDelayedNavigate()

  const {
    canEdit,
    loading,
    canViewClassificationData,
    metadata: {
      userFactsheetRoles,
      published,
      enableEvaluationFileUpload,
      factsheetId: factsheetIdToCheckPermissionFor,
    },
  } = usePermissionsForFactsheetDetailsDescriptionPage(factsheet_id)

  const [factsheetEffectivenessClassificationData, setFactsheetEffectivenessClassificationData] =
    useState<FactsheetClassificationDataState>({
      categories: [],
      reason: undefined,
    })

  useEffect(() => {
    const fetchData = async () => {
      const { factsheetClassificationCategories, values } = await fetchFactsheetEffectivenessClassificationData(
        factsheet_id,
        urqlClient,
        locale,
      )

      const data: FactsheetClassificationDataState = {
        categories: [],
        reason: values.classificationReason,
      }

      factsheetClassificationCategories.forEach((category) => {
        const scoreId = values.scores[`${CATEGORY_ID_PREFIX}${category.categoryId}`]
          ? values.scores[`${CATEGORY_ID_PREFIX}${category.categoryId}`]?.toString()
          : undefined

        const scoreName = category.scores.find((score) => score.value?.toString() === scoreId)?.label

        data.categories.push({
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          score: scoreName,
        })
      })

      setFactsheetEffectivenessClassificationData(data)
    }

    // fetch only if the user can see this data
    if (canViewClassificationData) {
      fetchData()
    }
  }, [factsheet_id, urqlClient, locale, canViewClassificationData])

  const [{ data, error, fetching }] = useQuery<{
    factsheet_by_pk: Query_Root['factsheet_by_pk']
  }>({
    query: enableEvaluationFileUpload ? fetchFactsheetByIdWithEffectivenessEvaluations : fetchFactsheetByIdQuery,
    variables: { id: factsheet_id },
    pause:
      published === null || enableEvaluationFileUpload === null || factsheetIdToCheckPermissionFor !== factsheet_id,
  })

  const factsheet = data?.factsheet_by_pk

  const factsheetEffectivenessEvaluationIds = useMemo(
    () => factsheet?.factsheet_effectiveness_evaluations?.map((e) => e.id) ?? [],
    [factsheet?.factsheet_effectiveness_evaluations],
  )

  const [{ data: dataFiles, error: errorFiles }] = useQuery<
    {
      file: Query_Root['file']
    },
    {
      fileType: string
      entityType: string
      entityIds: number[]
    }
  >({
    query: queryFiles,
    variables: {
      fileType: FILE_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
      entityType: ENTITY_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
      entityIds: factsheetEffectivenessEvaluationIds,
    },
    pause:
      enableEvaluationFileUpload === null ||
      !enableEvaluationFileUpload ||
      !data ||
      factsheetEffectivenessEvaluationIds.length === 0,
  })

  if (error || errorFiles) {
    notificationService.operationFailed()
  }

  const factsheetEvaluation: Array<Factsheet_Effectiveness_Evaluation> = useMemo(
    () => factsheet?.factsheet_effectiveness_evaluations ?? [],
    [factsheet?.factsheet_effectiveness_evaluations],
  )

  const factsheetEvaluationFiles: Array<GraphqlFile> = useMemo(() => dataFiles?.file ?? [], [dataFiles?.file])

  const evaluationFileItems: EvaluationFileItem[] = useMemo(
    () =>
      factsheetEvaluation.map((evaluation) => {
        const file = factsheetEvaluationFiles.find((f) => f.entity_id === evaluation.id)
        return mapToEvaluationFileItem(evaluation, file)
      }),
    [factsheetEvaluation, factsheetEvaluationFiles],
  )

  const relatedFactsheetClickHandler = useCallback(
    (factsheetId: number) => () => {
      window.open(ROUTES.FactsheetDescriptionRoot.nested.FactsheetDescription.params({ factsheetId }))
    },
    [],
  )

  const getFactsheetAttributeByLanguage = useCallback(
    (attribute: any): any => {
      return attribute?.[language]
    },
    [language],
  )

  const renderLanguageAttribute = useCallback(
    (field: string) => {
      return (
        getFactsheetAttributeByLanguage(field) && (
          <Card>
            <CardContent>
              <HtmlRenderer html={getFactsheetAttributeByLanguage(field)} />
            </CardContent>
          </Card>
        )
      )
    },
    [getFactsheetAttributeByLanguage],
  )

  const getUserText = useCallback(
    (user: Maybe<User> | undefined) => {
      if (!user) {
        return ''
      } else if (user.status === USER_STATUS.ACTIVE) {
        return `${user.first_name} ${user.last_name}, ${user.email}`
      } else {
        return `${getMessage('label.user.unknown')}, ${user.email}`
      }
    },
    [getMessage],
  )

  const handleEditDescription = useCallback(
    (field: string) => {
      const route = ROUTES.FactsheetDescriptionRoot.params({ factsheetId })
      navigate(`${route}/${field}/edit`)
    },
    [factsheetId, navigate],
  )

  const handleEditEvidenceOfEffectivenessFileUploadDescription = useCallback(() => {
    const route = ROUTES.FactsheetDescriptionRoot.nested.FactsheetEvidenceOfEffectivenessFileUploadEdit.params({
      factsheetId,
    })
    navigate(route)
  }, [factsheetId, navigate])

  const showProjectLead = useCallback(
    (factsheet: Factsheet): boolean => {
      if (
        factsheet.workflow_status === FACTSHEET_WORKFLOW_STATUS.CONCEPT ||
        factsheet.workflow_status === FACTSHEET_WORKFLOW_STATUS.REVISION ||
        factsheet.workflow_status === FACTSHEET_WORKFLOW_STATUS.ACCEPTED
      ) {
        if (Utils.isFactsheetCollaboratorRole(userFactsheetRoles)) {
          return true
        }

        if (Utils.isExternalRole(userRoles) && !Utils.isFactsheetCollaboratorRole(userFactsheetRoles)) {
          return false
        }

        if (Utils.isCantonalRole(user.roles)) {
          return false
        }
      }

      return true
    },
    [userRoles, userFactsheetRoles, user.roles],
  )

  const canDownloadEvidenceOfEffectivenessFile = useCallback(
    (fileItem: EvaluationFileItem): boolean => {
      if (Utils.isInternalRole(userRoles)) {
        return true
      }

      const isProjectLeader = userFactsheetRoles?.includes(FACTSHEET_USER_ROLES.ADMIN)
      if (isProjectLeader) {
        return true
      }

      const isCollaborator = Utils.isFactsheetCollaboratorRole(userFactsheetRoles)
      if (isCollaborator) {
        return fileItem.publish
      }

      return fileItem.publish && published === true
    },
    [published, userFactsheetRoles, userRoles],
  )

  return (
    <ScreenLayout
      title={get(factsheet?.title, language, '')}
      hasSecondLevelNavigation={true}
      actions={<FactsheetExportMenu />}
    >
      <PageLayout>
        <>
          {!loading && !fetching && factsheet && (
            <>
              {/* Basic information Section*/}
              <Section
                id={FACTSHEET_DESCRIPTION_EDIT_OPTION.BASIC_INFO}
                title={getMessage('label.factsheet.basic.information')}
                actionButton={
                  <EditButton
                    origin="content"
                    color="primary"
                    onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.BASIC_INFO)}
                    hidden={!canEdit}
                  />
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.basic.information" />}
              >
                <Card>
                  <CardContent>
                    <Box>
                      <ReadOnlyTextField text={getMessage('label.factsheet.id')}>
                        {`${getMessage('label.factsheet.id.character')}${factsheet.id}`}
                      </ReadOnlyTextField>
                      <ReadOnlySelection text={getMessage('label.factsheet.type')}>
                        {factsheet.application_type &&
                          getMessage(
                            `label.application_type.factsheet.${factsheet.application_type.code.toLowerCase()}`,
                          )}
                      </ReadOnlySelection>
                      <ReadOnlyTextField text={getMessage('label.level')}>
                        {getMessage(`label.level.${factsheet.level}`)}
                      </ReadOnlyTextField>
                      <ReadOnlySelection text={getMessage('label.modules')}>
                        {factsheet.modules &&
                          factsheet?.modules.map((module: any) => (
                            <Typography key={module}>{getMessage(`label.module.description.${module}`)}</Typography>
                          ))}
                      </ReadOnlySelection>
                      <ReadOnlyTextField text={getMessage('label.factsheet.published')}>
                        {getMessage(`label.factsheet.published.${factsheet.published}`)}
                      </ReadOnlyTextField>
                      <ReadOnlyTextField text={getMessage('label.factsheet.version')}>
                        {factsheet.version}
                      </ReadOnlyTextField>
                      <ReadOnlyTextField text={getMessage('label.organization')}>
                        {factsheet.organization}
                      </ReadOnlyTextField>
                      <ReadOnlyTextField text={getMessage('label.contact')}>
                        {factsheet?.contact && <HtmlRenderer html={factsheet?.contact} />}
                      </ReadOnlyTextField>
                      {showProjectLead(factsheet) && (
                        <ReadOnlyTextField text={getMessage('label.project.leader')} isLast>
                          {getUserText(factsheet.project_leader_user)}
                        </ReadOnlyTextField>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Section>

              {/* Title and description Section */}
              <Section
                id={FACTSHEET_DESCRIPTION_EDIT_OPTION.TITLE_AND_DESCRIPTION}
                title={getMessage('label.title.and.description')}
                actionButton={
                  <EditButton
                    origin="content"
                    color="primary"
                    onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.TITLE_AND_DESCRIPTION)}
                    hidden={!canEdit}
                  />
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.title_and_description" />}
              >
                <Card>
                  <CardContent>
                    <ReadOnlyTextField text={getMessage('label.title')}>
                      {getFactsheetAttributeByLanguage(factsheet.title)}
                    </ReadOnlyTextField>
                    <ReadOnlyTextField text={getMessage('label.description')} isLast>
                      {getFactsheetAttributeByLanguage(factsheet.description) && (
                        <HtmlRenderer html={getFactsheetAttributeByLanguage(factsheet.description)} />
                      )}
                    </ReadOnlyTextField>
                  </CardContent>
                </Card>
              </Section>

              {/* Factsheet copied Section */}
              <Section
                title={getMessage('label.factsheet.original')}
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.original" />}
                actionButton={<></>}
              >
                {factsheet.original_factsheet ? (
                  <S.Card.Container>
                    <S.Card.Content
                      onClick={relatedFactsheetClickHandler(factsheet.original_factsheet.id)}
                      tabIndex={0}
                      onKeyDown={(event: { key: string }) => {
                        if (['Enter', ' '].includes(event.key)) {
                          relatedFactsheetClickHandler(factsheet.original_factsheet!.id)()
                        }
                      }}
                    >
                      <IdChip
                        size="small"
                        label={`${getMessage('label.factsheet.id.character')}${factsheet.original_factsheet.id}`}
                      />
                      <Typography variant="subtitle2">
                        {getFactsheetAttributeByLanguage(factsheet.original_factsheet.title)}
                      </Typography>
                    </S.Card.Content>
                  </S.Card.Container>
                ) : (
                  <S.Card.Container>
                    <S.Card.Content>
                      <DefaultSectionTypography
                        noEntriesMessageKey={getMessage('label.no.original.factsheets')}
                        $standAlone={true}
                      />
                    </S.Card.Content>
                  </S.Card.Container>
                )}
              </Section>

              {/* Important Insights Section */}
              <Section
                id={FACTSHEET_DESCRIPTION_EDIT_OPTION.IMPORTANT_INSIGHTS}
                title={getMessage('label.factsheet.important_insights')}
                actionButton={
                  <EditButton
                    origin="content"
                    color="primary"
                    onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.IMPORTANT_INSIGHTS)}
                    hidden={!canEdit}
                  />
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.important_insights" />}
              >
                {renderLanguageAttribute(factsheet.important_insights)}
              </Section>

              {/* Strength Section */}
              <Section
                id={FACTSHEET_DESCRIPTION_EDIT_OPTION.STRENGTH}
                title={getMessage('label.factsheet.strength')}
                actionButton={
                  <EditButton
                    origin="content"
                    color="primary"
                    onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.STRENGTH)}
                    hidden={!canEdit}
                  />
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.strength" />}
              >
                {renderLanguageAttribute(factsheet.strength)}
              </Section>

              {/* Further Information Section */}
              <Section
                id={FACTSHEET_DESCRIPTION_EDIT_OPTION.FURTHER_INFORMATION}
                title={getMessage('label.factsheet.further_information')}
                actionButton={
                  <EditButton
                    origin="content"
                    color="primary"
                    onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.FURTHER_INFORMATION)}
                    hidden={!canEdit}
                  />
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.further_information" />}
              >
                {renderLanguageAttribute(factsheet.further_information)}
              </Section>

              {/* Evidence of Effectiveness Section */}
              {enableEvaluationFileUpload === true ? (
                // new file upload enabled section
                <Section
                  id={FACTSHEET_DESCRIPTION_EDIT_OPTION.EVIDENCE_OF_EFFECTIVENESS}
                  title={getMessage('label.factsheet.evidence_of_effectiveness')}
                  helpAndInstructions={
                    <HelpAndInstructions labelKey="label.help.factsheet.edit.evidence_of_effectiveness.file_upload.section.title" />
                  }
                  actionButton={
                    <EditButton
                      origin="content"
                      color="primary"
                      onClick={() => handleEditEvidenceOfEffectivenessFileUploadDescription()}
                      hidden={!canEdit}
                    />
                  }
                >
                  <Card>
                    <CardContent>
                      <Stack>
                        <Box>
                          {factsheetEffectivenessClassificationData.categories.length > 0 && (
                            <Box mb={2}>
                              {factsheetEffectivenessClassificationData.categories.map((category) => (
                                <Box key={category.categoryId} mb={1}>
                                  <ReadOnlyTextField text={category.categoryName}>{category.score}</ReadOnlyTextField>
                                </Box>
                              ))}

                              <Box mt={1}>
                                <ReadOnlyTextField
                                  text={getMessage(
                                    'label.factsheet.edit.evidence_of_effectiveness.classification.reason',
                                  )}
                                >
                                  {factsheetEffectivenessClassificationData.reason !== '' && (
                                    <HtmlRenderer html={factsheetEffectivenessClassificationData.reason} />
                                  )}
                                </ReadOnlyTextField>
                              </Box>
                            </Box>
                          )}
                        </Box>
                        <Box>
                          <EvidenceOfEffectivenessFilesGrid
                            editMode={false}
                            showDownloadButton={true}
                            showPublishCheckbox={published === false}
                            canDownloadFile={canDownloadEvidenceOfEffectivenessFile}
                            loading={loading}
                            rows={evaluationFileItems}
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Section>
              ) : (
                // old description
                <Section
                  id={FACTSHEET_DESCRIPTION_EDIT_OPTION.EVIDENCE_OF_EFFECTIVENESS}
                  title={getMessage('label.factsheet.evidence_of_effectiveness')}
                  actionButton={
                    <EditButton
                      origin="content"
                      color="primary"
                      onClick={() => handleEditDescription(FACTSHEET_DESCRIPTION_EDIT_OPTION.EVIDENCE_OF_EFFECTIVENESS)}
                      hidden={!canEdit}
                    />
                  }
                  helpAndInstructions={
                    <HelpAndInstructions labelKey="label.help.factsheet.evidence_of_effectiveness" />
                  }
                >
                  {renderLanguageAttribute(factsheet.evidence_of_effectiveness)}
                </Section>
              )}
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
